import { useState, useEffect } from 'react';
import api from '../utils/api';
import CourseCard from '../components/course/CourseCard';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useNavigate, Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { schemaGraph, bundleProductSchema } from '../utils/schema';

const US_STATES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas',
  CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho',
  IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas',
  KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
  MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma',
  OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah',
  VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia',
  WI: 'Wisconsin', WY: 'Wyoming',
};

const STATE_REGULATORS = {
  AZ: { name: 'ADRE', hours: 24 },
  CA: { name: 'DRE', hours: 45 },
  TX: { name: 'TREC', hours: 18 },
  FL: { name: 'DBPR', hours: 14 },
  NV: { name: 'NRED', hours: 24 },
  CO: { name: 'DORA', hours: 24 },
  UT: { name: 'DRE', hours: 18 },
};

const getRegulator = (code) => STATE_REGULATORS[code] || { name: 'State Board', hours: 24 };

const CREDIT_TYPES = [
  { value: '', label: 'All Categories' },
  { value: 'core', label: 'Core' },
  { value: 'elective', label: 'Elective' },
  { value: 'agency', label: 'Agency' },
  { value: 'commissioners_standard', label: "Commissioner's Standard" },
  { value: 'contract_law', label: 'Contract Law' },
  { value: 'disclosure', label: 'Disclosure' },
  { value: 'fair_housing', label: 'Fair Housing' },
  { value: 'real_estate_legal_issues', label: 'RE Legal Issues' },
  { value: 'general', label: 'General' },
];

export default function CoursesPage() {
  const { user } = useAuth();
  const { addToCart, addBundle, items } = useCart();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  useSEO({
    title: 'Arizona CE Courses | Steady Education',
    description: 'Browse ADRE-approved Arizona real estate continuing education courses. Self-paced online learning, instant certificates, starting at $9.99.',
    url: 'https://steadyeducation.com/courses',
    jsonLd: schemaGraph({
      mainSchemas: [bundleProductSchema()],
      breadcrumb: [
        { name: 'Home', url: 'https://steadyeducation.com' },
        { name: 'Courses', url: 'https://steadyeducation.com/courses' },
      ],
    }),
  });
  const [allCourses, setAllCourses] = useState([]);
  const [enrolled, setEnrolled] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [addingBundle, setAddingBundle] = useState(false);
  const [bundleMsg, setBundleMsg] = useState('');
  // Always init 'AZ' so first render matches prerendered HTML; hydrate from
  // localStorage after mount to avoid hydration mismatches.
  const [selectedState, setSelectedState] = useState('AZ');

  useEffect(() => {
    const saved = localStorage.getItem('se_state');
    if (saved && saved !== 'AZ') setSelectedState(saved);
    const onStateChange = (e) => setSelectedState(e.detail);
    window.addEventListener('stateChange', onStateChange);
    return () => window.removeEventListener('stateChange', onStateChange);
  }, []);

  const stateName = US_STATES[selectedState] || 'Your State';
  const regulator = getRegulator(selectedState);
  const hasCoursesForState = loading || courses.length > 0;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [coursesRes, enrolledRes] = await Promise.all([
          api.get('/courses'),
          user ? api.get('/users/me/courses') : Promise.resolve({ data: { courses: [] } }),
        ]);
        const all = coursesRes?.data?.courses || [];
        setAllCourses(all);
        setCourses(all.filter(c => c.state === selectedState));
        setEnrolled((enrolledRes?.data?.courses || []).map(c => c.course_id));
        setEnrolledCourses(enrolledRes?.data?.courses || []);
      } catch (err) {
        // Non-fatal: keeps prerendering and offline scenarios from throwing to the page
        console.warn('Courses load failed:', err?.message);
      } finally { setLoading(false); }
    };
    fetchData();
  }, [user, selectedState]);

  const handleBuyBundle = async () => {
    setAddingBundle(true);
    setBundleMsg('');
    try {
      await addBundle('AZ');
      navigate('/cart');
    } catch (err) {
      setBundleMsg(err.response?.data?.error || 'Could not add bundle');
    } finally { setAddingBundle(false); }
  };

  const filtered = courses.filter(c => {
    const matchesFilter = !filter || c.credit_type === filter;
    const matchesSearch = !search || c.title.toLowerCase().includes(search.toLowerCase()) || c.short_description?.toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const bundlePrice = 49.99;
  const fullPrice = courses.reduce((s, c) => s + parseFloat(c.price || 0), 0);

  // Build a status map for enrolled courses
  const statusByCourseId = {};
  enrolledCourses.forEach(c => { statusByCourseId[c.course_id] = c.status; });

  // Hide bundle if user owns all courses AND any are still in progress (not completed)
  // Show bundle again if all courses are completed (renewal flow)
  const ownsAllCourses = courses.length > 0 && courses.every(c => enrolled.includes(c.id));
  const allCompleted = ownsAllCourses && courses.every(c => statusByCourseId[c.id] === 'completed');
  const showBundle = (!ownsAllCourses || allCompleted) && courses.length > 0;

  return (
    <div className="page-wrapper">
      <div className="container">
        {/* Page header */}
        <div style={styles.pageHeader}>
          <h1 className="page-title">{stateName} CE Courses</h1>
          <p className="page-subtitle">{regulator.name}-approved continuing education for {stateName} real estate licensees</p>
        </div>

        {!hasCoursesForState && !loading ? (
          /* Coming soon for other states */
          <div style={styles.comingSoon}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🚀</div>
            <h2 style={styles.comingSoonTitle}>{stateName} Courses Coming Soon!</h2>
            <p style={styles.comingSoonDesc}>
              We are working hard to bring {regulator.name}-approved CE courses to {stateName} real estate licensees.
              Enter your email below to be notified when {stateName} courses launch.
            </p>
            <div style={styles.notifyForm}>
              <input className="input" placeholder="your@email.com" style={{ maxWidth: 280 }} />
              <button className="btn btn-primary">Notify Me</button>
            </div>
            <p style={{ fontSize: 14, color: 'var(--color-text-muted)', marginTop: 16 }}>
              Currently offering courses in: <strong>Arizona</strong>
            </p>
          </div>
        ) : (
          <>
            {/* Bundle banner — hidden when user owns all courses */}
            {showBundle && <div style={styles.bundleBanner}>
              <div style={styles.bundleLeft}>
                <div style={styles.bundleTag}>⭐ Best Value</div>
                <h2 style={styles.bundleTitle}>Complete {stateName} CE Package</h2>
                <p style={styles.bundleDesc}>
                  All {courses.length} {regulator.name}-approved courses — everything you need to complete your {regulator.hours}-hour {stateName} CE renewal.
                </p>
                <div style={styles.bundleFeatures}>
                  {[
                    `✓ All ${regulator.hours} required hours covered`,
                    '✓ All 2025 required courses included',
                    '✓ Instant access after purchase',
                    '✓ Certificates upon completion',
                  ].map(f => <span key={f} style={styles.bundleFeature}>{f}</span>)}
                </div>
              </div>
              <div style={styles.bundleRight}>
                <div style={styles.bundlePricing}>
                  <div style={styles.bundleWas}>Individual total: ${fullPrice.toFixed(2)}</div>
                  <div style={styles.bundlePrice}>${bundlePrice.toFixed(2)}</div>
                  <div style={styles.bundleSave}>Save ${(fullPrice - bundlePrice).toFixed(2)}</div>
                </div>
                <button style={styles.bundleBtn} onClick={handleBuyBundle} disabled={addingBundle}>
                  {addingBundle ? 'Adding...' : `Buy ${stateName} Package — $${bundlePrice.toFixed(2)}`}
                </button>
                {bundleMsg && <div style={styles.bundleMsg}>{bundleMsg}</div>}
                <div style={styles.bundleNote}>🔒 Secure checkout via Stripe</div>
              </div>
            </div>}

            {/* Divider */}
            {showBundle && <div style={styles.divider}>
              <span style={styles.dividerText}>Or purchase individual courses below</span>
            </div>}

            {/* Filters */}
            <div style={styles.filters}>
              <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
                <svg style={styles.searchIcon} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
                <input className="input" style={{ paddingLeft: 36, fontSize: 13 }} placeholder="Search courses..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <div style={styles.filterTabs}>
                {CREDIT_TYPES.map(t => (
                  <button key={t.value} style={{ ...styles.filterTab, ...(filter === t.value ? styles.filterTabActive : {}) }} onClick={() => setFilter(t.value)}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Grid */}
            {loading ? (
              <div className="loading-wrapper"><div className="spinner" /></div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
                <h3>No courses found</h3>
                <p style={{ color: 'var(--color-text-muted)' }}>Try adjusting your search or filter</p>
              </div>
            ) : (
              <div style={styles.grid}>
                {filtered.map(c => <CourseCard key={c.id} course={c} owned={enrolled.includes(c.id)} status={statusByCourseId[c.id]} compact />)}
              </div>
            )}
          </>
        )}

        {/* ADRE info */}
        <div style={styles.adreBox}>
          <div style={{ fontSize: 24, flexShrink: 0 }}>🏛️</div>
          <div>
            <strong>All courses approved by the {stateName} real estate regulatory authority</strong>
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: '4px 0 0' }}>
              School License S25-0002 • 5227 N 7th Street, Phoenix, AZ 85014 • help@steadyeducation.com
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  pageHeader: { textAlign: 'center', marginBottom: 32 },
  comingSoon: { textAlign: 'center', padding: '60px 40px', background: 'var(--tan-50)', border: '2px dashed #b39e89', borderRadius: 20, marginBottom: 40 },
  comingSoonTitle: { fontFamily: 'var(--font-display)', fontSize: 26, color: 'var(--forest-700)', marginBottom: 12 },
  comingSoonDesc: { fontSize: 16, color: 'var(--slate-600)', lineHeight: 1.7, maxWidth: 500, margin: '0 auto 24px' },
  notifyForm: { display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' },
  bundleBanner: { background: 'linear-gradient(135deg, #1a3a3a 0%, #1d4142 100%)', borderRadius: 20, padding: '36px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 40, flexWrap: 'wrap', marginBottom: 40, boxShadow: '0 8px 32px rgba(29,65,66,0.2)' },
  bundleLeft: { flex: 1, minWidth: 280 },
  bundleTag: { display: 'inline-block', background: 'rgba(179,158,137,0.2)', color: '#d4b896', fontSize: 12, fontWeight: 700, padding: '4px 12px', borderRadius: 999, marginBottom: 12, border: '1px solid rgba(179,158,137,0.3)' },
  bundleTitle: { fontFamily: 'var(--font-display)', fontSize: 24, color: 'white', marginBottom: 10, lineHeight: 1.2 },
  bundleDesc: { fontSize: 14, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, marginBottom: 16 },
  bundleFeatures: { display: 'flex', flexDirection: 'column', gap: 6 },
  bundleFeature: { fontSize: 13, color: 'rgba(255,255,255,0.85)', fontWeight: 500 },
  bundleRight: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, minWidth: 220 },
  bundlePricing: { textAlign: 'center' },
  bundleWas: { fontSize: 13, color: 'rgba(255,255,255,0.5)', textDecoration: 'line-through', marginBottom: 4 },
  bundlePrice: { fontSize: 48, fontWeight: 800, color: 'white', lineHeight: 1 },
  bundleSave: { fontSize: 13, fontWeight: 700, color: '#d4b896', marginTop: 4 },
  bundleBtn: { background: '#b39e89', color: 'white', border: 'none', borderRadius: 10, padding: '14px 28px', fontSize: 15, fontWeight: 800, cursor: 'pointer', width: '100%', transition: 'all 0.15s' },
  bundleMsg: { fontSize: 13, fontWeight: 600, color: '#d4b896', textAlign: 'center' },
  bundleNote: { fontSize: 12, color: 'rgba(255,255,255,0.5)', textAlign: 'center' },
  divider: { textAlign: 'center', marginBottom: 28, borderTop: '1px solid #b39e89', paddingTop: 0, position: 'relative', marginTop: 8 },
  dividerText: { background: 'white', padding: '0 16px', fontSize: 13, color: 'var(--color-text-muted)', fontWeight: 500, position: 'relative', top: -10, display: 'inline-block' },
  filters: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 },
  searchIcon: { position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--slate-400)', pointerEvents: 'none' },
  filterTabs: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  filterTab: { padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 500, border: '1.5px solid #b39e89', background: 'white', color: 'var(--forest-600)', cursor: 'pointer', transition: 'all 0.15s' },
  filterTabActive: { background: 'var(--forest-500)', color: 'white', borderColor: 'var(--forest-500)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16, marginBottom: 40 },
  adreBox: { display: 'flex', alignItems: 'flex-start', gap: 14, background: 'var(--tan-50)', border: '1px solid #b39e89', borderRadius: 12, padding: '16px 20px' },
};